import React, { useState } from 'react';

import { Helmet } from 'react-helmet';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Container, Stack, Typography } from '@mui/material';

import { type PromoFeatureKey } from '@luxuryescapes/contract-svc-promo';

import PageSubheader from '../../Common/Elements/PageSubheader';
import VoucherAccountPreview from '../../Vouchers/VoucherAccountPreview';
import { withTenant } from '../../hoc';
import MyVouchersTesterContainer from '../MyVouchersTester/MyVouchersTesterContainer';
import usePromoTesterUrl from '../hooks/usePromoTesterUrl';

import FriendsAndFamilyPromoTester from './FriendsAndFamilyPromoTester';
import FriendsAndFamilyUsageWidget from './FriendsAndFamilyUsageWidget';
import MarketingRouterEvaluator from './MarketingRouterEvaluator';
import PromoActivitySummary from './PromoActivitySummary';
import PromoByToggleTester from './PromoByToggleTester';
import PromoExperimentalFeatures from './PromoExperimentalFeatures';
import PromoMigrations from './PromoMigrations';
import PromoSimulatorMulti from './PromoSimulatorMulti';
import PromoTester from './PromoTester';

const experimentalFeatureUrls = {
  'voucher-presentation-config-show-call-to-action-button-in-details': '/marketing/voucher-display-config',
  'voucher-presentation-config-show-condition-evaluation-when-failing': '/marketing/voucher-display-config',
  'voucher-presentation-config-show-condition-evaluation-when-passing': '/marketing/voucher-display-config',
  'voucher-presentation-config-show-full-debug-for-internal-users': '/marketing/voucher-display-config',
  'voucher-presentation-config': '/marketing/voucher-display-config',
  'channel-markup-v2': '/marketing/channel-markup-v2',
  'channel-markup-v2-conditions': '/marketing/channel-markup-v2',
} as const satisfies Readonly<Partial<Record<PromoFeatureKey, string>>>;

interface Props {
  tenant: App.Tenant;
}

function PromoTesterContainer({ tenant }: Props) {
  const [orderCustomerUserId, setOrderCustomerUserId] = useState<string | undefined>();
  const { urlState, updateUrlState } = usePromoTesterUrl({
    brand: tenant.brand,
  });

  return (
    <Container>
      <Helmet>
        <title>Promo tools</title>
      </Helmet>
      <PageSubheader title="Promo tools" />
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Accordion
          defaultExpanded={Boolean(urlState.orderId || urlState.codeName || urlState.voucherIds)}
          slotProps={{ transition: { mountOnEnter: true } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="h2" variant="h6">
              Promo simulator
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PromoTester
              initOrderId={urlState.orderId}
              initCodeName={urlState.codeName}
              initVoucherIdsRaw={urlState.voucherIds}
              initApplyWithOrignalUserAccount={urlState.applyWithOrignalUserAccount}
              initIgnoreLuxPlusPricing={urlState.ignoreLuxPlusPricing ?? true}
              tenant={tenant}
              onOrderCustomerUserIdChange={setOrderCustomerUserId}
              // URL management props
              urlState={urlState}
              updateUrlState={updateUrlState}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion slotProps={{ transition: { mountOnEnter: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="h2" variant="h6">
              Promo Simulator (Multi)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PromoSimulatorMulti tenant={tenant} />
          </AccordionDetails>
        </Accordion>
        <Accordion slotProps={{ transition: { mountOnEnter: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="h2" variant="h6">
              My vouchers tester
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <MyVouchersTesterContainer tenant={tenant} orderCustomerUserId={orderCustomerUserId} />
          </AccordionDetails>
        </Accordion>
        <Accordion slotProps={{ transition: { mountOnEnter: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="h2" variant="h6">
              Voucher account preview
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <VoucherAccountPreview tenant={tenant} />
          </AccordionDetails>
        </Accordion>
        <Accordion slotProps={{ transition: { mountOnEnter: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="h2" variant="h6">
              Marketing router evaluator
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <MarketingRouterEvaluator tenant={tenant} />
          </AccordionDetails>
        </Accordion>
        <Accordion slotProps={{ transition: { mountOnEnter: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="h2" variant="h6">
              Friends &amp; Family promo request
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <FriendsAndFamilyUsageWidget tenant={tenant} />
              <FriendsAndFamilyPromoTester tenant={tenant} />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion slotProps={{ transition: { mountOnEnter: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="h2" variant="h6">
              Experimental promo features
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PromoExperimentalFeatures featureUrls={experimentalFeatureUrls} />
          </AccordionDetails>
        </Accordion>
        <Accordion slotProps={{ transition: { mountOnEnter: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="h2" variant="h6">
              Promo and voucher toggle search
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PromoByToggleTester />
          </AccordionDetails>
        </Accordion>
        <Accordion slotProps={{ transition: { mountOnEnter: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="h2" variant="h6">
              Recent promo activity
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PromoActivitySummary />
          </AccordionDetails>
        </Accordion>
        <Accordion slotProps={{ transition: { mountOnEnter: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component="h2" variant="h6">
              Dev
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PromoMigrations />
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Container>
  );
}

export default withTenant(PromoTesterContainer);
